import { createContext, ReactNode, useContext } from "react"
import { useSession } from "next-auth/react"

const rolesLevels: Record<string, number> = {
  OWNER: 5,
  ADMIN: 4,
  EDITOR: 3,
  USER: 2,
  CHAT_ONLY: 1,
}
export type Role = keyof typeof rolesLevels // Derive the Role type
const AllRoles: Role[] = Object.keys(rolesLevels) as Role[]

export const getFilteredRoles = (excludeRoles: Role[] = ["OWNER"]): Role[] => {
  return AllRoles.filter((role) => !excludeRoles.includes(role))
}

/**
 * Defines the shape of the RoleContext.
 */
interface RoleContextType {
  /** Checks if the current user is an owner of the app, alias Superadmin */
  isAppOwner: () => boolean
  /** Checks if the current user is in a specified organization with a certain role. */
  isInOrganization: (organizationId: string, role?: string) => boolean
  /** Checks if the current user is allowed to access a specified organization with a certain role. */
  isAllowed: (requiredRole: Role, organizationId?: string) => boolean
  /** Gets the role of the user for a specified organization. */
  getRole: (organizationId: string) => Role | null
}

/**
 * Creates a context for handling roles.
 */
const RoleContext = createContext<RoleContextType | null>(null)

/**
 * Properties for the RoleProvider component.
 */
interface RoleProviderProps {
  children: ReactNode
}

/**
 * Provides a context for roles for the child components.
 *
 * @param {RoleProviderProps} children - The child components of RoleProvider.
 * @returns {ReactElement} - The RoleProvider wrapping the child components.
 */
export function RoleProvider({ children }: RoleProviderProps) {
  const { data: clientSession, status } = useSession()
  const user = clientSession?.user

  /**
   * Checks if the current user is an owner of the app.
   *
   * @returns {boolean} - Whether the user is an owner.
   */
  const isAppOwner = () => {
    const userRoles = user?.UserRole || []
    return userRoles.some((role) => role.role === "OWNER")
  }

  /**
   * Checks if the current user is in a specified organization with a certain role.
   *
   * @param {string} organizationId - The ID of the organization.
   * @param {string} [role="OWNER"] - The role of the user.
   * @returns {boolean} - Whether the user is in the organization with the role.
   */
  const isInOrganization = (organizationId: string = "", role = "OWNER") => {
    organizationId =
      organizationId !== "" ? organizationId : user?.organizationId || ""
    if (organizationId === "") {
      return false
    }

    const userOrganizationRoles = user?.UserOrganizationRole || []
    return userOrganizationRoles.some(
      (orgRole) =>
        isAppOwner() ||
        (orgRole.role === role && orgRole.Organization.id === organizationId)
    )
  }

  /**
   * Checks if the current user is allowed to access a specified organization with a certain role.
   *
   * @param {string} organizationId - The ID of the organization.
   * @param {Role} [requiredRole="USER"] - The role required to access the organization.
   * @returns {boolean} - Whether the user is allowed to access the organization with the required role.
   */
  const isAllowed = (
    requiredRole: Role = "USER",
    organizationId?: string
  ): boolean => {
    organizationId =
      organizationId !== undefined
        ? organizationId
        : user?.UserOrganizationRole[0].Organization.id || ""
    if (organizationId === "") {
      return false
    }
    const userOrganizationRoles = user?.UserOrganizationRole || []

    const requiredLevel = rolesLevels[requiredRole]
    return userOrganizationRoles.some(
      (orgRole) =>
        isAppOwner() ||
        (orgRole.Organization.id === organizationId &&
          rolesLevels[orgRole.role as Role] >= requiredLevel)
    )
  }

  /**
   * Returns the UserOrganizationRole for the specified organization.
   *
   * @param {string} organizationId - The ID of the organization.
   * @returns {Role | null} - The role of the user for the specified organization, or null if not found.
   */
  const getRole = (organizationId: string): Role | null => {
    if (!user) return null

    const userOrganizationRole = user?.UserOrganizationRole?.find(
      (orgRole) => orgRole.Organization.id === organizationId
    )
    return userOrganizationRole?.role as Role | null
  }

  const value = {
    isAppOwner,
    isInOrganization,
    isAllowed,
    getRole,
  }

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>
}

/**
 * A hook to get the current RoleContext.
 *
 * @throws {Error} - If the hook is not used within a RoleProvider.
 * @returns {RoleContextType} - The current RoleContext.
 */
export function useRoles() {
  const context = useContext(RoleContext)
  if (!context) {
    throw new Error("useRoles must be used within a RoleProvider")
  }
  return context
}
